import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { t } from 'ttag';
import { useShopListQuery, useOperatorQuestionnaireQuery, useLazyOperatorHashQuery } from 'api/index.js';
import { deselectShop } from 'store/ducks/operatorHome.js';
import useShallowEqualSelector from 'hooks/useShallowEqualSelector.js';
import { DDS_URL } from 'constants.js';
import imgLogo from 'static/logos/delv.png';
import IconShop from 'static/icons/shop.svg';
import './index.scss';

const NavItem = ({ to, label, hasShop, noShopRequired, isCurrent, onClick, className = '', ...rest }) => (
    <li className={`nav-item ${className}`} inert={(!hasShop && !noShopRequired) ? '' : undefined} {...rest}>
        {to ? (
            <Link to={to} aria-current={isCurrent ? 'page' : undefined} className='nav-link'>
                {label}
            </Link>
        ) : (
            <button type='button' onClick={onClick} className='nav-link'>
                {label}
            </button>
        )}
    </li>
);

const ShopButton = ({ accessProfile, shopName }) => {
    const dispatch = useDispatch();

    if(!accessProfile) return null;

    const onClick = () => dispatch(deselectShop());

    return (
        <Link
            to='/operator/select-station'
            onClick={onClick}
            title={shopName ? t`(COMPONENT_OP_NAV)_CURRENT_SITE_BTN_TOOLTIP` : undefined}
            className='current-site botton has-icon botton-variant-plain botton-size-s'
        >
            <IconShop />
            <span className='current-site-name'>
                {shopName || t`(COMPONENT_OP_NAV)_SELECT_SITE_BTN_LABEL`}
            </span>
        </Link>
    );
};

const OperatorNavigator = () => {
    const isOperator = useShallowEqualSelector(state => state.auth.isOperator);
    const isEmployee = useShallowEqualSelector(state => state.auth.isEmployee);
    const isContractor = useShallowEqualSelector(state => state.auth.isContractor);
    const isDealer = useShallowEqualSelector(state => state.auth.isDealer);
    const isBatteryProvider = useShallowEqualSelector(state => state.auth.isBatteryProvider);
    const isBatteryReceiver = useShallowEqualSelector(state => state.auth.isBatteryReceiver);
    const isRecycler = useShallowEqualSelector(state => state.auth.isRecycler);
    const isImporter = useShallowEqualSelector(state => state.auth.isImporter);
    const shopName = useShallowEqualSelector(state => state.operatorHome.shopName);
    const shops = useShallowEqualSelector(state => state.operatorHome.shops);
    const hasAccessToBatteryForm = useShallowEqualSelector(state => state.operatorHome.hasAccessToBatteryForm);

    const [fetchOperatorHash] = useLazyOperatorHashQuery();

    useShopListQuery(undefined, { skip: !isOperator || shops });

    useOperatorQuestionnaireQuery(
        { type: 'company_battery' },
        { skip: hasAccessToBatteryForm !== null || !isOperator },
    );

    const accessRecycling = isOperator || isEmployee;
    const accessMgmt = isOperator || isContractor;
    const accessBatteries = isRecycler;
    const accessMarket = isBatteryReceiver || isRecycler;
    const accessBatteryWizard = isBatteryProvider || isImporter;
    const accessProfile = accessMgmt || isBatteryProvider || isBatteryReceiver || isEmployee || isRecycler;
    const noShopRequired = true;

    const onDdsClick = async () => {
        const { data } = await fetchOperatorHash();
        if(!data) return;
        window.location.assign(`${DDS_URL}/?operator_hash=${encodeURIComponent(data.name_hash)}`);
    };

    const navItems = () => [
        // TRANS: Operator sidebar navigation labels
        ...(accessRecycling ? [
            { to: '/operator/recycle', label: t`ENTER_NEW_VEHICLE` },
            { to: '/operator/incoming', label: t`INCOMING` },
            { to: '/operator/available', label: t`AVAILABLE` },
            { to: '/operator/process', label: t`IN_PROCESS` },
        ] : []),
        ...(accessBatteryWizard ? [
            { to: '/operator/batteries/add', label: t`(OPERATOR_NAVLINK)_BATTERIES_ADD`, noShopRequired },
            { to: '/operator/batteries/added', label: t`(OPERATOR_NAVLINK)_BATTERIES_ADDED`, noShopRequired },
        ] : []),
        ...(isImporter ? [
            // OOS for now t: esimies 2026-06-10
            // { to: '/operator/batteries/incoming', label: t`(OPERATOR_NAVLINK)_BATTERIES_INCOMING`, noShopRequired },
            { to: '/operator/batteries/recycled', label: t`(OPERATOR_NAVLINK)_BATTERIES_RECYCLED`, noShopRequired },
        ] : []),
        accessBatteries && { to: '/operator/batteries/process', label: t`(OPERATOR_NAVLINK)_BATTERIES_PROCESS` },
        accessMarket && (
            { to: '/operator/batteries/market', label: t`(OPERATOR_NAVLINK)_BATTERIES_MARKET`, noShopRequired }
        ),
        accessRecycling && { to: '/operator/responsibility', label: t`(OPERATOR_NAVLINK)_RESPONSIBILITY` },
        isOperator && { label: t`(OPERATOR_NAVLINK)_DDS`, onClick: onDdsClick },
        isDealer && { to: '/operator/orders', label: t`(OPERATOR_NAVLINK)_ORDERS`, noShopRequired },
        hasAccessToBatteryForm && (
            { to: '/operator/forms/battery', label: t`(OPERATOR_NAVLINK)_BATTERY_FORM`, noShopRequired }
        ),
        accessMgmt && { to: '/operator/management', label: t`USER_MANAGEMENT` },
        accessProfile && { to: '/operator/profile', label: t`PROFILE`, noShopRequired },
        { to: '/logout', label: t`LOGOUT`, noShopRequired },
    ];

    const { pathname } = window.location;

    return (
        <nav className='operator-navigator'>
            <Link to='/operator' className='nav-logo-wrap'>
                <img src={imgLogo} alt='DELV' />
            </Link>
            <ShopButton accessProfile={accessProfile} shopName={shopName} />
            <menu className='nav-menu'>
                {navItems().map(item => item && (
                    <NavItem
                        key={item.to ?? item.label}
                        isCurrent={!!item.to && (pathname === item.to || pathname.startsWith(`${item.to}/`))}
                        hasShop={!!shopName}
                        {...item}
                    />
                ))}
            </menu>
        </nav>
    );
};

export default OperatorNavigator;
